import type { ReactNode } from "react";
import { AlertTriangle, CheckCircle2, Rows3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type {
  BulkImportRecord,
  CreateBulkImportBatchResult,
} from "@/features/bulk-imports";
import { BulkImportStatusBadge } from "./BulkImportStatusBadge";

interface BulkImportPreviewProps {
  result: CreateBulkImportBatchResult | null;
  onOpenBatch: (batchId: string) => void;
}

export function BulkImportPreview({
  result,
  onOpenBatch,
}: BulkImportPreviewProps) {
  if (!result) {
    return (
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Preview</CardTitle>
          <CardDescription>
            La validacion aparecera despues de subir un archivo.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            Sin lote cargado en esta sesion.
          </div>
        </CardContent>
      </Card>
    );
  }

  const visibleRows = result.rows.slice(0, 50);
  const hasErrors = result.validation.errorRowCount > 0;

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <CardTitle className="text-lg">Preview validado</CardTitle>
            <CardDescription>{result.batch.sourceFilename}</CardDescription>
          </div>
          <Button
            variant="outline"
            onClick={() => onOpenBatch(result.batch.id)}
          >
            Abrir detalle
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-4">
          <SummaryItem
            label="Filas"
            value={result.validation.rowCount}
            icon={<Rows3 className="h-4 w-4" />}
          />
          <SummaryItem
            label="Validas"
            value={result.validation.validRowCount}
            icon={<CheckCircle2 className="h-4 w-4" />}
          />
          <SummaryItem
            label="Errores"
            value={result.validation.errorRowCount}
            tone={hasErrors ? "danger" : undefined}
            icon={<AlertTriangle className="h-4 w-4" />}
          />
          <SummaryItem
            label="Omitidas"
            value={result.validation.skippedRowCount}
          />
        </div>

        <div className="overflow-x-auto rounded-md border">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b bg-muted/40 text-left text-muted-foreground">
                <th className="px-3 py-2 font-medium">Fila</th>
                <th className="px-3 py-2 font-medium">Estado</th>
                <th className="px-3 py-2 font-medium">Grupo</th>
                <th className="px-3 py-2 font-medium">Raw</th>
                <th className="px-3 py-2 font-medium">Normalizado</th>
                <th className="px-3 py-2 font-medium">Errores</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row) => (
                <tr key={row.id} className="border-b last:border-0">
                  <td className="px-3 py-3 font-mono text-xs">
                    {row.sourceRowNumber}
                  </td>
                  <td className="px-3 py-3">
                    <BulkImportStatusBadge status={row.status} />
                  </td>
                  <td className="px-3 py-3 text-muted-foreground">
                    {row.groupKey ?? "-"}
                  </td>
                  <td className="px-3 py-3">
                    <RecordPreview record={row.rawData} />
                  </td>
                  <td className="px-3 py-3">
                    <RecordPreview record={row.normalizedData} />
                  </td>
                  <td className="px-3 py-3">
                    <ValidationMessages
                      errors={row.validationResult?.errors ?? []}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {result.rows.length > visibleRows.length && (
          <p className="text-xs text-muted-foreground">
            Mostrando las primeras {visibleRows.length} de {result.rows.length}{" "}
            filas.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function SummaryItem({
  label,
  value,
  icon,
  tone,
}: {
  label: string;
  value: number;
  icon?: ReactNode;
  tone?: "danger";
}) {
  return (
    <div className="rounded-md border p-3">
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{label}</span>
        {icon}
      </div>
      <p
        className={
          tone === "danger"
            ? "mt-2 text-xl font-semibold text-destructive"
            : "mt-2 text-xl font-semibold"
        }
      >
        {value}
      </p>
    </div>
  );
}

function RecordPreview({ record }: { record: BulkImportRecord | null }) {
  if (!record) {
    return <span className="text-muted-foreground">-</span>;
  }

  const entries = Object.entries(record)
    .filter(
      ([, value]) => value !== null && value !== undefined && value !== "",
    )
    .slice(0, 5);

  if (entries.length === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <dl className="max-w-[320px] space-y-1">
      {entries.map(([key, value]) => (
        <div key={key} className="grid grid-cols-[110px_1fr] gap-2">
          <dt className="truncate text-xs text-muted-foreground">{key}</dt>
          <dd className="truncate text-xs">{formatValue(value)}</dd>
        </div>
      ))}
    </dl>
  );
}

function ValidationMessages({
  errors,
}: {
  errors: readonly { message: string; field?: string }[];
}) {
  if (errors.length === 0) {
    return <span className="text-muted-foreground">-</span>;
  }

  return (
    <ul className="max-w-[280px] space-y-1 text-xs text-destructive">
      {errors.slice(0, 3).map((error, index) => (
        <li key={`${error.field ?? "row"}-${index}`} className="leading-5">
          {error.field ? `${error.field}: ` : ""}
          {error.message}
        </li>
      ))}
      {errors.length > 3 && <li>+{errors.length - 3} mas</li>}
    </ul>
  );
}

function formatValue(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (value instanceof Date) return value.toISOString();
  return JSON.stringify(value);
}
